import { Injectable, InjectionToken, inject } from '@angular/core';

import { DslService, UserService } from '@frontend/vanilla/core';
import { Observable, Subject, of, switchMap, take } from 'rxjs';

import { CashierResourceService } from '../cashier-resource.service';
import { CashierConfig } from '../cashier.client-config';
import { QuickDepositAction, QuickDepositEvent, QuickDepositOptions } from './quick-deposit.models';

/**
 * @whatItDoes Opens quick deposit overlay
 *
 * @howToUse
 *
 * ```this.quickDepositService.open();```
 *
 * @description
 *
 * Cashier will be opened within the modal dialog. User balance gets updated if deposit is made.
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class QuickDepositService {
    readonly events = new Subject<QuickDepositEvent>();

    private readonly cashierConfig = inject(CashierConfig);
    private readonly userService = inject(UserService);
    private readonly cashierResourceService = inject(CashierResourceService);
    private readonly dslService = inject(DslService);

    open(options: QuickDepositOptions) {
        this.events.next({ action: QuickDepositAction.Open, options });
    }

    isEnabled(): Observable<boolean> {
        if (!this.userService.isAuthenticated) {
            return of(false);
        }

        if (this.cashierConfig.isQuickDepositEnabledCondition) {
            return this.dslService.evaluateExpression<boolean>(this.cashierConfig.isQuickDepositEnabledCondition).pipe(
                take(1),
                switchMap((isEnabled: boolean) => (isEnabled ? this.cashierResourceService.quickDepositEnabled() : of(false))),
            );
        }

        if (!this.cashierConfig.isQuickDepositEnabled) {
            return of(false);
        }

        return this.cashierResourceService.quickDepositEnabled();
    }
}

export const QUICK_DEPOSIT_OPTIONS = new InjectionToken<QuickDepositOptions>('QUICK_DEPOSIT_OPTIONS');
